/**
 * Size-Chart Modal — CFSN-516.
 *
 * Theme-owned replacement for the Kiwi Size Chart app. Triggered by elements
 * with [data-size-chart-trigger][data-page-handle][data-product-title].
 * Fetches `/pages/<handle>?section_id=size-chart-content` on first open per
 * handle, caches the HTML in-memory, and reuses it on subsequent opens.
 *
 * The 19 size-chart pages were authored against the Kiwi app's HTML structure
 * (`ks-*` classes, `kiwi-active` flag, `data-unit-values` cells, lazysizes
 * `data-src` images). We keep the markup as-is so the content team can paste
 * existing Kiwi output verbatim — but on injection we normalize everything to
 * our own naming so neither CSS nor inspector references a third-party app:
 *
 *   ks-*        →  size-chart-*
 *   kiwi-active →  is-active
 *   data-src    →  src
 *
 * The unit toggle (cm / inches / etc.) is wired up after normalization: each
 * toggle owns the cells inside its enclosing `.size-chart-table-content-wrapper`,
 * and clicking a label rewrites those cells from the JSON map stored in their
 * `data-unit-values` attribute.
 */
(function () {
  if (customElements.get('size-chart-modal')) return;

  class SizeChartModal extends HTMLElement {
    connectedCallback() {
      this.cache = new Map();
      this.contentEl = this.querySelector('[data-size-chart-content]');
      this.titleEl = this.querySelector('[data-size-chart-title]');
      this.contentInitialHTML = this.contentEl ? this.contentEl.innerHTML : '';
      this.helper = new ModalHelper(this, {
        overlaySelector: '[data-modal-overlay]',
        closeSelector: '[data-modal-close]',
        focusTarget: '[data-modal-close]',
      });
      this.boundHandleClick = this.handleTriggerClick.bind(this);
      document.addEventListener('click', this.boundHandleClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundHandleClick);
      this.helper?.destroy();
    }

    async handleTriggerClick(e) {
      const trigger = e.target.closest('[data-size-chart-trigger]');
      if (!trigger) return;
      e.preventDefault();
      const handle = trigger.dataset.pageHandle;
      if (!handle) return;

      if (this.titleEl) {
        this.titleEl.textContent = trigger.dataset.productTitle || '';
      }
      this.helper.open();
      await this.loadContent(handle);
    }

    async loadContent(handle) {
      if (!this.contentEl) return;

      // Cache key includes the locale prefix so a mid-session locale switch
      // (without a full reload) wouldn't serve stale cross-locale HTML. Dawn
      // reloads on locale switch today, but keying by URL hardens against
      // future routing changes.
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const url = `${root}pages/${handle}?section_id=size-chart-content`;
      const cacheKey = url;

      if (this.cache.has(cacheKey)) {
        this.contentEl.innerHTML = this.cache.get(cacheKey);
        this.enhanceContent();
        return;
      }

      this.contentEl.innerHTML = this.contentInitialHTML;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const rawHtml = await response.text();
        const html = this.normalizeHTML(rawHtml);
        this.cache.set(cacheKey, html);
        this.contentEl.innerHTML = html;
        this.enhanceContent();
      } catch (err) {
        console.error('SizeChartModal: fetch failed', err);
        this.contentEl.textContent = '';
      }
    }

    /**
     * Rename Kiwi-era classes to our own in a single DOM pass so what hits the
     * page is consistent with our CSS. Returns serialized HTML (the input is
     * a string from fetch, so we parse → mutate → reserialize).
     */
    normalizeHTML(rawHtml) {
      const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
      doc.querySelectorAll('[class]').forEach((el) => {
        const renamed = Array.from(el.classList).map((c) => {
          if (c === 'kiwi-active') return 'is-active';
          if (c.startsWith('ks-')) return 'size-chart-' + c.slice(3);
          return c;
        });
        el.className = renamed.join(' ');
      });
      // Section-rendering API wraps the section in a <section>-like element;
      // we want our `.size-chart-content` wrapper preserved so the typography
      // rules in component-size-chart-modal.css scoped to that class fire.
      const wrapper = doc.querySelector('.size-chart-content');
      return wrapper ? wrapper.outerHTML : doc.body.innerHTML;
    }

    enhanceContent() {
      if (!this.contentEl) return;
      this.normalizeImages(this.contentEl);
      this.bindUnitToggles(this.contentEl);
    }

    /**
     * Convert lazysizes-style `data-src` attributes to plain `src` so images
     * render without pulling in the lazysizes library.
     */
    normalizeImages(container) {
      container.querySelectorAll('img[data-src]').forEach((img) => {
        if (!img.getAttribute('src')) {
          img.setAttribute('src', img.dataset.src);
        }
        img.setAttribute('loading', 'lazy');
      });
    }

    /**
     * Wire each unit toggle to the cells inside its enclosing section.
     * A "section" is a `.size-chart-table-content-wrapper`; each toggle only
     * affects cells within that wrapper, so multiple unit-aware tables on the
     * same page stay independent.
     *
     * The Kiwi-era markup encodes units as opaque numeric keys in
     * `data-unit-values='{"0":"…","1":"…"}'` — by Kiwi convention `0` is
     * inches, `1` is cm. The labels' `data-value` matches the keys, so we
     * never have to know which is which here.
     *
     * Promotes the `<div>` labels to keyboard-operable buttons (role/tabindex/
     * aria-pressed + Enter/Space handling) since the pasted Kiwi HTML uses
     * non-focusable divs.
     */
    bindUnitToggles(container) {
      const select = (label, labels, cells) => {
        const value = label.dataset.value;
        labels.forEach((l) => {
          const active = l === label;
          l.classList.toggle('is-active', active);
          l.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        cells.forEach((cell) => {
          try {
            const map = JSON.parse(cell.dataset.unitValues);
            if (map && map[value] !== undefined) {
              cell.textContent = map[value];
            }
          } catch (e) {
            /* malformed JSON — leave cell as-is */
          }
        });
      };

      container.querySelectorAll('.size-chart-unit-toggle').forEach((toggle) => {
        const labels = Array.from(toggle.querySelectorAll('[data-value]'));
        if (labels.length === 0) return;
        const scope = toggle.closest('.size-chart-table-content-wrapper') || container;
        const cells = Array.from(scope.querySelectorAll('[data-unit-values]'));

        toggle.setAttribute('role', 'group');
        labels.forEach((label) => {
          label.setAttribute('role', 'button');
          label.setAttribute('tabindex', '0');
          label.setAttribute(
            'aria-pressed',
            label.classList.contains('is-active') ? 'true' : 'false'
          );

          label.addEventListener('click', () => select(label, labels, cells));
          label.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              select(label, labels, cells);
            }
          });
        });
      });
    }
  }

  customElements.define('size-chart-modal', SizeChartModal);
})();
